import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import AgencyDashboard from './pages/AgencyDashboard';
import MasterAgencyDashboard from './pages/MasterAgencyDashboard';
import CollegeManagement from './pages/CollegeManagement';
import AgencyManagement from './pages/AgencyManagement';
import ApplicationsList from './pages/ApplicationsList';
import ApplicationForm from './pages/ApplicationForm';
import UserManagement from './pages/UserManagement';
import BankDetailsPage from './pages/BankDetailsPage';
import DropdownControls from './pages/DropdownControls';
import AdminPaymentDetails from './pages/AdminPaymentDetails';
import EscalationMatrix from './pages/EscalationMatrix';
import OfflinePayments from './pages/OfflinePayments';
import ChangePassword from './pages/ChangePassword';
import DownloadCenter from './pages/DownloadCenter';
import PaymentQRManagement from './pages/PaymentQRManagement';
import TrashManagement from './pages/TrashManagement';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  const getDashboard = () => {
    if (user?.role === 'agency') return <AgencyDashboard />;
    if (user?.role === 'master_agency') return <MasterAgencyDashboard />;
    return <AdminDashboard />;
  };

  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {getDashboard()}
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/colleges"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <CollegeManagement />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/agencies"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin', 'master_agency']}>
            <AgencyManagement />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <ApplicationsList />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/apply"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ApplicationForm />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin', 'master_agency']}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/bank-details"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <BankDetailsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dropdown-controls"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <DropdownControls />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/payment-details"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <AdminPaymentDetails />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/escalation-matrix"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <EscalationMatrix />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/offline-payments"
        element={
          <ProtectedRoute>
            <OfflinePayments />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/download-center"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <DownloadCenter />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/payment-qr"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <PaymentQRManagement />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/trash"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
            <TrashManagement />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;